import { useMemo } from "react";
import { usePanelTexture } from "./usePanelTexture";
import { RO, Z } from "../theme3d";
import { usePanelOccluded } from "./PanelOcclusionContext";

export interface PanelIllustrationProps {
  url: string | null;
  maxWidth: number;
  maxHeight: number;
  position?: [number, number, number];
  occluded?: boolean;
}

// Aspect-fit plane for a narrative illustration. Renders nothing while the
// texture is loading, missing, or failed (usePanelTexture never suspends —
// see its module doc) — a slow or absent image just leaves an empty spot
// on the panel rather than risking the panel itself.
export function PanelIllustration({
  url,
  maxWidth,
  maxHeight,
  position = [0, 0, 0],
  occluded,
}: PanelIllustrationProps) {
  const resolvedOccluded = usePanelOccluded(occluded);
  const texture = usePanelTexture(url);
  const [x, y, z] = position;

  const size = useMemo((): [number, number] | null => {
    const image = texture?.image as { width?: number; height?: number } | undefined;
    if (!image?.width || !image.height) return null;
    const aspect = image.width / image.height;
    const boxAspect = maxWidth / maxHeight;
    return aspect >= boxAspect
      ? [maxWidth, maxWidth / aspect]
      : [maxHeight * aspect, maxHeight];
  }, [texture, maxWidth, maxHeight]);

  if (!texture || !size) return null;

  return (
    <mesh
      position={[x, y, z + Z.image]}
      renderOrder={resolvedOccluded ? RO.panelImage : RO.overlayBacking + 1}
    >
      <planeGeometry args={size} />
      <meshBasicMaterial
        map={texture}
        transparent
        depthTest={resolvedOccluded}
        depthWrite={false}
      />
    </mesh>
  );
}
